import React, { useState, useEffect } from 'react';
import { 
  LineChart, 
  BarChart, 
  Bar,
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, 
  Globe, 
  FileText, 
  DollarSign, 
  Clock, 
  Filter, 
  Search, 
  AlertTriangle, 
  ArrowUp, 
  ArrowDown,
  RefreshCw,
  ChevronDown,
  Calendar,
  Zap,
  TrendingDown,
  Info,
  Coffee,
  Wheat,
  Banana,
  Loader,
  Check,
  X,
  ArrowRight,
  MapPin,
  ShieldAlert,
  Brain,
  Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import AnimatedSection from '../components/AnimatedSection';
import { Card, Badge, Button } from '../components/ui';

// Sample commodity data for real-time displays
const commoditiesData = [
  { 
    id: 'coffee-arabica', 
    name: 'Coffee (Arabica)', 
    currentPrice: 215.35, 
    unit: 'USD/lb',
    change: '+2.3%',
    isUp: true,
    volume: '125,000 tons',
    icon: <Coffee className="h-5 w-5" />,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    data: [
      { month: 'Jan', price: 180 },
      { month: 'Feb', price: 190 },
      { month: 'Mar', price: 205 },
      { month: 'Apr', price: 210 },
      { month: 'May', price: 215.35 }
    ],
    forecast: [
      { month: 'Jun', price: 220 },
      { month: 'Jul', price: 225 },
      { month: 'Aug', price: 228 },
      { month: 'Sep', price: 232 }
    ],
    factors: [
      { name: 'Weather in Brazil', impact: 'high', trend: 'positive' },
      { name: 'Demand from Asia', impact: 'medium', trend: 'positive' },
      { name: 'Shipping Costs', impact: 'low', trend: 'negative' }
    ],
    insights: "Arabica coffee prices continue their upward trend due to lower-than-expected yields in Brazil and increased global demand. Climate conditions in key growing regions remain favorable for the next quarter, suggesting a potential stabilization but not a significant price drop.",
    regions: [
      { name: "Brazil", production: "35%", trend: "declining", price: "high" },
      { name: "Colombia", production: "15%", trend: "stable", price: "premium" },
      { name: "Ethiopia", production: "10%", trend: "growing", price: "premium" },
      { name: "Uganda", production: "5%", trend: "growing", price: "standard" }
    ]
  },
  { 
    id: 'coffee-robusta', 
    name: 'Coffee (Robusta)', 
    currentPrice: 180.45, 
    unit: 'USD/lb',
    change: '+1.8%',
    isUp: true,
    volume: '95,000 tons',
    icon: <Coffee className="h-5 w-5" />,
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    data: [
      { month: 'Jan', price: 165 },
      { month: 'Feb', price: 170 },
      { month: 'Mar', price: 175 },
      { month: 'Apr', price: 177 },
      { month: 'May', price: 180.45 }
    ],
    forecast: [
      { month: 'Jun', price: 182 },
      { month: 'Jul', price: 185 },
      { month: 'Aug', price: 187 },
      { month: 'Sep', price: 190 }
    ],
    factors: [
      { name: 'Vietnam Production', impact: 'high', trend: 'stable' },
      { name: 'Instant Coffee Demand', impact: 'medium', trend: 'positive' },
      { name: 'Shipping Delays', impact: 'low', trend: 'negative' }
    ],
    insights: "Robusta coffee continues to see steady price increases, though less dramatic than Arabica. Vietnam's production remains stable, while global demand for instant coffee, which heavily uses Robusta, continues to grow especially in emerging markets.",
    regions: [
      { name: "Vietnam", production: "40%", trend: "stable", price: "standard" },
      { name: "Brazil", production: "15%", trend: "growing", price: "standard" },
      { name: "Indonesia", production: "12%", trend: "stable", price: "standard" },
      { name: "Uganda", production: "7%", trend: "growing", price: "standard" }
    ]
  },
  { 
    id: 'wheat', 
    name: 'Wheat', 
    currentPrice: 275.80, 
    unit: 'USD/ton',
    change: '-0.7%',
    isUp: false,
    volume: '780,000 tons',
    icon: <Wheat className="h-5 w-5" />,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    data: [
      { month: 'Jan', price: 290 },
      { month: 'Feb', price: 285 },
      { month: 'Mar', price: 280 },
      { month: 'Apr', price: 278 },
      { month: 'May', price: 275.80 }
    ],
    forecast: [
      { month: 'Jun', price: 274 },
      { month: 'Jul', price: 272 },
      { month: 'Aug', price: 275 },
      { month: 'Sep', price: 280 }
    ],
    factors: [
      { name: 'Ukraine Situation', impact: 'high', trend: 'stabilizing' },
      { name: 'US Harvest', impact: 'medium', trend: 'positive' },
      { name: 'Global Reserves', impact: 'medium', trend: 'stable' }
    ],
    insights: "Wheat prices are gradually declining as global supplies improve. Favorable weather conditions in major producing regions and a strong US harvest outlook are contributing to this trend. However, ongoing geopolitical tensions in Eastern Europe continue to introduce volatility.",
    regions: [
      { name: "Russia", production: "20%", trend: "uncertain", price: "discount" },
      { name: "United States", production: "15%", trend: "growing", price: "standard" },
      { name: "Canada", production: "12%", trend: "stable", price: "standard" },
      { name: "Australia", production: "8%", trend: "growing", price: "premium" }
    ]
  },
  { 
    id: 'cocoa', 
    name: 'Cocoa', 
    currentPrice: 4821.00, 
    unit: 'USD/ton',
    change: '+5.1%',
    isUp: true,
    volume: '110,000 tons',
    icon: <Coffee className="h-5 w-5" />,
    color: 'text-amber-800',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    data: [
      { month: 'Jan', price: 4200 },
      { month: 'Feb', price: 4350 },
      { month: 'Mar', price: 4500 },
      { month: 'Apr', price: 4600 },
      { month: 'May', price: 4821 }
    ],
    forecast: [
      { month: 'Jun', price: 4900 },
      { month: 'Jul', price: 4950 },
      { month: 'Aug', price: 5000 },
      { month: 'Sep', price: 5100 }
    ],
    factors: [
      { name: 'West African Weather', impact: 'high', trend: 'negative' },
      { name: 'Global Chocolate Demand', impact: 'high', trend: 'positive' },
      { name: 'Supply Chain Disruptions', impact: 'medium', trend: 'negative' }
    ],
    insights: "Cocoa prices have surged to multi-year highs due to weather concerns in West Africa and robust global chocolate demand. Production shortfalls in Côte d'Ivoire and Ghana, which together account for over 60% of global production, are likely to continue supporting high prices throughout the year.",
    regions: [
      { name: "Côte d'Ivoire", production: "40%", trend: "declining", price: "standard" },
      { name: "Ghana", production: "20%", trend: "declining", price: "premium" },
      { name: "Ecuador", production: "8%", trend: "growing", price: "premium" },
      { name: "Nigeria", production: "5%", trend: "stable", price: "standard" }
    ]
  },
  { 
    id: 'banana', 
    name: 'Banana', 
    currentPrice: 1.25, 
    unit: 'USD/kg',
    change: '-0.8%',
    isUp: false,
    volume: '1.2M tons',
    icon: <Banana className="h-5 w-5" />,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    data: [
      { month: 'Jan', price: 1.30 },
      { month: 'Feb', price: 1.28 },
      { month: 'Mar', price: 1.27 },
      { month: 'Apr', price: 1.26 },
      { month: 'May', price: 1.25 }
    ],
    forecast: [
      { month: 'Jun', price: 1.24 },
      { month: 'Jul', price: 1.23 },
      { month: 'Aug', price: 1.25 },
      { month: 'Sep', price: 1.27 }
    ],
    factors: [
      { name: 'Disease Resistance', impact: 'high', trend: 'positive' },
      { name: 'Latin American Production', impact: 'medium', trend: 'stable' },
      { name: 'European Demand', impact: 'low', trend: 'stable' }
    ],
    insights: "Banana prices remain relatively stable with a slight downward trend. Improved disease resistance in major producing regions is helping to stabilize supply. However, climate change continues to pose long-term challenges to consistent production.",
    regions: [
      { name: "Ecuador", production: "25%", trend: "stable", price: "standard" },
      { name: "Philippines", production: "15%", trend: "stable", price: "standard" },
      { name: "Colombia", production: "10%", trend: "growing", price: "standard" },
      { name: "Costa Rica", production: "8%", trend: "stable", price: "premium" }
    ]
  }
];

// Global market news
const marketNews = [
  {
    id: 'news-1',
    title: 'Coffee Prices Hit Five-Year High Amid Brazilian Drought Concerns',
    category: 'market',
    impact: 'High',
    date: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    content: "Coffee futures surged to a five-year high today as prolonged drought conditions in Brazil's key growing regions raise concerns about next year's crop yield. Analysts predict potential supply shortages could push prices even higher in coming months.",
    affectedCommodities: ['coffee-arabica', 'coffee-robusta']
  },
  {
    id: 'news-2',
    title: 'East African Cocoa Initiative Aims to Diversify Global Supply Chain',
    category: 'policy',
    impact: 'Medium',
    date: new Date(Date.now() - 1000 * 60 * 60 * 5), // 5 hours ago
    content: "A new international development program is investing $50 million to establish cocoa production in Uganda and Tanzania. The initiative aims to diversify the global cocoa supply chain beyond traditional West African sources, potentially affecting long-term market dynamics.",
    affectedCommodities: ['cocoa']
  },
  {
    id: 'news-3',
    title: 'China\'s Wheat Imports Expected to Increase Amid Domestic Shortfall',
    category: 'market',
    impact: 'Medium',
    date: new Date(Date.now() - 1000 * 60 * 60 * 8), // 8 hours ago
    content: "China is projected to increase its wheat imports by 15% this year following lower-than-expected domestic harvests. This increased demand from the world's most populous nation could put upward pressure on global wheat prices in the third quarter.",
    affectedCommodities: ['wheat']
  },
  {
    id: 'news-4',
    title: 'New Fungal Strain Threatens Central American Banana Production',
    category: 'disaster',
    impact: 'High',
    date: new Date(Date.now() - 1000 * 60 * 60 * 10), // 10 hours ago
    content: "Scientists have identified a new strain of Panama disease in Guatemala that could potentially impact up to 80% of banana exports from Central America. The highly contagious soil-borne fungus poses a significant threat to global banana supplies.",
    affectedCommodities: ['banana']
  },
  {
    id: 'news-5',
    title: 'EU to Implement Stricter Sustainability Requirements for Agricultural Imports',
    category: 'policy',
    impact: 'Medium',
    date: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    content: "The European Union has announced new regulations requiring all agricultural imports to meet enhanced sustainability and ethical production standards by 2026. The move is expected to significantly impact global supply chains and potentially increase costs for non-compliant producers.",
    affectedCommodities: ['coffee-arabica', 'coffee-robusta', 'cocoa', 'wheat', 'banana']
  }
];

// Seasonal calendar data
const seasonalCalendarData = {
  crops: ['Coffee', 'Cocoa', 'Wheat', 'Maize', 'Rice', 'Banana'],
  months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  plantingSeasons: {
    'Coffee': ['Jan', 'Feb', 'Mar'],
    'Cocoa': ['May', 'Jun', 'Jul'],
    'Wheat': ['Sep', 'Oct', 'Nov', 'Mar', 'Apr'],
    'Maize': ['Mar', 'Apr', 'May'],
    'Rice': ['Mar', 'Apr', 'May', 'Jun'],
    'Banana': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  },
  harvestSeasons: {
    'Coffee': ['Oct', 'Nov', 'Dec'],
    'Cocoa': ['Oct', 'Nov', 'Dec', 'Jan', 'Feb'],
    'Wheat': ['Jul', 'Aug', 'Sep'],
    'Maize': ['Aug', 'Sep', 'Oct'],
    'Rice': ['Sep', 'Oct', 'Nov'],
    'Banana': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  }
};

const MarketInsightsPage: React.FC = () => {
  const [selectedCommodity, setSelectedCommodity] = useState(commoditiesData[0]);
  const [activeTab, setActiveTab] = useState('price-trends');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  const [subscribeError, setSubscribeError] = useState('');
  const [subscribeEmail, setSubscribeEmail] = useState('');

  // Filter news by category and search term
  const filteredNews = marketNews.filter(news => {
    const matchesCategory = activeCategory === 'all' || news.category === activeCategory;
    const matchesSearch = searchTerm === '' || 
                          news.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          news.content.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Effect to simulate loading real-time data
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  // Function to simulate refreshing data
  const handleRefreshData = () => {
    setRefreshing(true);
    
    setTimeout(() => {
      setRefreshing(false);
      setLastUpdate(new Date());
    }, 2000);
  };

  // Function to handle newsletter subscription
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subscribeEmail) {
      setSubscribeError('Please enter your email');
      return;
    }
    
    setSubscribing(true);
    setSubscribeError('');
    
    try {
      // Here you would normally make an API call to subscribe the user
      // For this demo, we'll just simulate a successful subscription
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubscribeSuccess(true);
      setSubscribeEmail('');
      
      // Reset success message after delay
      setTimeout(() => {
        setSubscribeSuccess(false);
      }, 5000);
    } catch (error) {
      setSubscribeError('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-avn-purple-900 to-blue-900 text-white">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://images.unsplash.com/photo-1526304640581-d334cdbbf45e" 
            alt="Market data visualization"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container mx-auto px-4 py-16 relative z-10">
          <AnimatedSection>
            <div className="max-w-4xl">
              <Badge 
                variant="purple" 
                className="inline-flex mb-6 bg-white/20 backdrop-blur-sm px-4 py-2 text-white"
                icon={<TrendingUp className="h-5 w-5" />}
              >
                Global Agricultural Markets
              </Badge>
              <h1 className="text-5xl font-bold mb-6">
                Market Insights & Intelligence
              </h1>
              <p className="text-xl opacity-90 mb-8 leading-relaxed">
                Stay ahead with real-time data, expert analysis, and forecasting for global agricultural commodities and markets.
              </p>
              
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  <span>Last updated: {lastUpdate.toLocaleTimeString()}</span>
                </div>
                
                <button 
                  onClick={handleRefreshData}
                  disabled={refreshing}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 transition-colors px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
                  <span>{refreshing ? 'Refreshing...' : 'Refresh Data'}</span>
                </button>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>

      {showSignupPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full m-4"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-avn-purple-100 p-3 rounded-xl">
                  <TrendingUp className="h-6 w-6 text-avn-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Market Intelligence</h2>
                  <p className="text-gray-600">Premium insights for informed decisions</p>
                </div>
              </div>
              <button 
                onClick={() => setShowSignupPrompt(false)}
                className="text-gray-400 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <h3 className="font-semibold mb-2">Unlock Premium Features</h3>
              <ul className="space-y-2">
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Real-time price alerts and notifications</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Detailed regional and commodity-specific reports</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>AI-powered market forecasting and predictions</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-5 w-5 text-green-500 mt-0.5" />
                  <span>Customizable dashboard for your key commodities</span>
                </li>
              </ul>
            </div>
            
            <div className="space-y-4">
              <Link
                to="/signup"
                className="block w-full bg-avn-purple-600 text-white py-3 px-6 rounded-lg text-center hover:bg-avn-purple-700 transition-colors"
              >
                Sign Up for Full Access
              </Link>
              <button
                onClick={() => setShowSignupPrompt(false)}
                className="block w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg text-center hover:bg-gray-200 transition-colors"
              >
                Continue with Limited Access
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <Loader className="h-10 w-10 text-avn-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-avn-purple-600 font-medium">Loading market data...</p>
            </div>
          </div>
        ) : (
          <>
            {/* Commodity Selection */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-8">
              <h2 className="text-xl font-bold mb-4">Key Agricultural Commodities</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {commoditiesData.map((commodity) => (
                  <button
                    key={commodity.id}
                    onClick={() => setSelectedCommodity(commodity)}
                    className={`p-4 rounded-lg border ${
                      selectedCommodity.id === commodity.id
                        ? 'bg-avn-purple-50 border-avn-purple-300 shadow'
                        : `${commodity.bgColor} ${commodity.borderColor} hover:shadow-md`
                    } transition-all`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-full ${
                        selectedCommodity.id === commodity.id
                          ? 'bg-avn-purple-100'
                          : 'bg-white'
                      }`}>
                        {commodity.icon}
                      </div>
                      <div className="text-left">
                        <h3 className="font-medium text-gray-900">{commodity.name}</h3>
                        <div className="flex items-center gap-1">
                          <span className="font-bold">${commodity.currentPrice}</span>
                          <span className="text-xs text-gray-500">{commodity.unit}</span>
                        </div>
                        <div className={`flex items-center text-xs ${
                          commodity.isUp ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {commodity.isUp ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                          <span>{commodity.change}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Left Column - Commodity Analysis */}
              <div className="lg:col-span-2 space-y-8">
                <Card>
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className={`p-3 rounded-full ${selectedCommodity.bgColor}`}>
                        {selectedCommodity.icon}
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold">{selectedCommodity.name}</h2>
                        <div className="flex items-center gap-3 text-gray-600">
                          <span>${selectedCommodity.currentPrice} {selectedCommodity.unit}</span>
                          <span className={`flex items-center ${
                            selectedCommodity.isUp ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {selectedCommodity.isUp ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                            {selectedCommodity.change}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Data as of {new Date().toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  {/* Tabs */}
                  <div className="border-b mb-6">
                    <div className="flex gap-4 -mb-px">
                      <button
                        onClick={() => setActiveTab('price-trends')}
                        className={`px-4 py-2 font-medium ${
                          activeTab === 'price-trends'
                            ? 'text-avn-purple-600 border-b-2 border-avn-purple-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Price Trends
                      </button>
                      <button
                        onClick={() => setActiveTab('market-factors')}
                        className={`px-4 py-2 font-medium ${
                          activeTab === 'market-factors'
                            ? 'text-avn-purple-600 border-b-2 border-avn-purple-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Market Factors
                      </button>
                      <button
                        onClick={() => setActiveTab('regional-analysis')}
                        className={`px-4 py-2 font-medium ${
                          activeTab === 'regional-analysis'
                            ? 'text-avn-purple-600 border-b-2 border-avn-purple-600'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        Regional Analysis
                      </button>
                    </div>
                  </div>
                  
                  {/* Tab Content */}
                  <div className="mb-4">
                    {activeTab === 'price-trends' && (
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Historical & Forecast Pricing</h3>
                        <div className="h-72">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={[...selectedCommodity.data, ...selectedCommodity.forecast]}
                              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                              <XAxis dataKey="month" />
                              <YAxis 
                                domain={[(dataMin: number) => Math.floor(dataMin * 0.95), (dataMax: number) => Math.ceil(dataMax * 1.05)]}
                                tickFormatter={(value) => `$${value}`}
                              />
                              <Tooltip 
                                formatter={(value) => [`$${value}`, 'Price']}
                                labelFormatter={(label) => `Month: ${label}`}
                              />
                              <Legend />
                              <Line 
                                type="monotone" 
                                dataKey="price" 
                                stroke="#9333ea" 
                                strokeWidth={2} 
                                dot={{ r: 4 }}
                                activeDot={{ r: 6 }}
                                name="Historical Price"
                                data={selectedCommodity.data}
                              />
                              <Line 
                                type="monotone" 
                                dataKey="price" 
                                stroke="#9333ea" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={{ r: 4, fill: '#9333ea' }}
                                name="Price Forecast" 
                                data={selectedCommodity.forecast}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                        
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-avn-purple-100 rounded-full mt-1">
                              <Brain className="h-5 w-5 text-avn-purple-600" />
                            </div>
                            <div>
                              <h4 className="font-medium">AI-Powered Market Insight</h4>
                              <p className="text-gray-600">
                                {selectedCommodity.insights}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'market-factors' && (
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Key Market Factors</h3>
                        <div className="space-y-4">
                          {selectedCommodity.factors.map((factor, index) => (
                            <div key={index} className="bg-gray-50 p-4 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium">{factor.name}</h4>
                                <Badge 
                                  variant={
                                    factor.impact === 'high' ? 'red' : 
                                    factor.impact === 'medium' ? 'amber' : 'blue'
                                  }
                                  className="capitalize"
                                >
                                  {factor.impact} impact
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className={`w-12 h-1.5 rounded-full ${
                                  factor.trend === 'positive' ? 'bg-green-500' :
                                  factor.trend === 'negative' ? 'bg-red-500' :
                                  factor.trend === 'stabilizing' ? 'bg-yellow-500' : 'bg-gray-500'
                                }`}></div>
                                <span className="text-sm text-gray-600 capitalize">{factor.trend} trend</span>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="bg-yellow-50 p-4 rounded-lg">
                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                                <span>Price Volatility</span>
                              </h4>
                              <div className="flex items-center gap-4">
                                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="bg-yellow-500 h-full rounded-full"
                                    style={{ width: `${selectedCommodity.id === 'cocoa' ? '85' : 
                                                      selectedCommodity.id === 'coffee-arabica' ? '70' : 
                                                      selectedCommodity.id === 'coffee-robusta' ? '55' : 
                                                      selectedCommodity.id === 'wheat' ? '45' : '30'}%` }}
                                  ></div>
                                </div>
                                <span className="font-bold">
                                  {selectedCommodity.id === 'cocoa' ? 'Very High' : 
                                   selectedCommodity.id === 'coffee-arabica' ? 'High' : 
                                   selectedCommodity.id === 'coffee-robusta' ? 'Moderate' : 
                                   selectedCommodity.id === 'wheat' ? 'Low' : 'Very Low'}
                                </span>
                              </div>
                            </div>
                            
                            <div className="bg-blue-50 p-4 rounded-lg">
                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                <TrendingUp className="h-5 w-5 text-blue-600" />
                                <span>Forecasting Confidence</span>
                              </h4>
                              <div className="flex items-center gap-4">
                                <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="bg-blue-500 h-full rounded-full"
                                    style={{ width: `${selectedCommodity.id === 'banana' ? '85' : 
                                                      selectedCommodity.id === 'wheat' ? '75' : 
                                                      selectedCommodity.id === 'coffee-robusta' ? '65' : 
                                                      selectedCommodity.id === 'coffee-arabica' ? '60' : '50'}%` }}
                                  ></div>
                                </div>
                                <span className="font-bold">
                                  {selectedCommodity.id === 'banana' ? 'Very High' : 
                                   selectedCommodity.id === 'wheat' ? 'High' : 
                                   selectedCommodity.id === 'coffee-robusta' ? 'Moderate' : 
                                   selectedCommodity.id === 'coffee-arabica' ? 'Moderate' : 'Low'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {activeTab === 'regional-analysis' && (
                      <div>
                        <h3 className="text-xl font-semibold mb-4">Regional Analysis</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Region
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Global Production
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Production Trend
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Price Category
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {selectedCommodity.regions.map((region, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center">
                                      <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                                      <div className="font-medium text-gray-900">{region.name}</div>
                                    </div>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {region.production}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                      region.trend === 'growing' ? 'bg-green-100 text-green-800' : 
                                      region.trend === 'declining' ? 'bg-red-100 text-red-800' : 
                                      region.trend === 'stable' ? 'bg-blue-100 text-blue-800' : 
                                      'bg-yellow-100 text-yellow-800'
                                    }`}>
                                      {region.trend}
                                    </span>
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 capitalize">
                                    {region.price}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        
                        <div className="mt-6 pt-6 border-t border-gray-200">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-blue-100 rounded-full mt-1">
                              <Globe className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <h4 className="font-medium">Global Market Context</h4>
                              <p className="text-gray-600">
                                {selectedCommodity.id === 'coffee-arabica' && 
                                  "Arabica coffee production is highly concentrated in specific regions with suitable altitude and climate. Brazil dominates production, but climate change is shifting production patterns, with growing regions in Africa gaining market share."}
                                {selectedCommodity.id === 'coffee-robusta' && 
                                  "Vietnam leads Robusta production globally, with relatively stable output year-over-year. East African nations like Uganda are experiencing steady growth in Robusta production, potentially shifting market dynamics in the coming years."}
                                {selectedCommodity.id === 'cocoa' && 
                                  "West African nations remain the dominant producers of cocoa, though facing significant challenges from climate change and aging farmer populations. Latin American producers are gaining market share with premium products."}
                                {selectedCommodity.id === 'wheat' && 
                                  "Wheat production is widely distributed globally, making the market more resilient to regional disruptions. However, geopolitical issues affecting major exporters like Russia can still cause significant price volatility."}
                                {selectedCommodity.id === 'banana' && 
                                  "Banana production has relatively stable output year-round but faces significant long-term challenges from plant diseases. Latin American countries dominate exports, while Asian production primarily serves domestic and regional markets."}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Related News */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Related News</h3>
                      <Link to="#news" className="text-avn-purple-600 text-sm hover:text-avn-purple-700 flex items-center gap-1">
                        <span>View All News</span>
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                    
                    <div className="space-y-4">
                      {marketNews
                        .filter(news => news.affectedCommodities.includes(selectedCommodity.id))
                        .slice(0, 2)
                        .map(news => (
                          <div key={news.id} className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2 mb-2">
                                  <Badge 
                                    variant={
                                      news.impact === 'High' ? 'red' : 
                                      news.impact === 'Medium' ? 'amber' : 'blue'
                                    }
                                  >
                                    {news.impact} Impact
                                  </Badge>
                                  <span className="text-sm text-gray-500">
                                    {news.date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                  </span>
                                </div>
                                <h4 className="font-medium mb-2">{news.title}</h4>
                                <p className="text-sm text-gray-600 line-clamp-2">{news.content}</p>
                              </div>
                              <button className="text-avn-purple-600 hover:text-avn-purple-700">
                                <Eye className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                </Card>
                
                {/* Seasonal Calendar */}
                <Card>
                  <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-avn-purple-600" />
                    <span>Global Seasonal Calendar</span>
                  </h2>
                  
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                            Crop
                          </th>
                          {seasonalCalendarData.months.map(month => (
                            <th key={month} className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                              {month}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {seasonalCalendarData.crops.map(crop => (
                          <tr key={crop} className="hover:bg-gray-50">
                            <td className="px-2 py-3 whitespace-nowrap">
                              <div className="font-medium text-gray-900">{crop}</div>
                            </td>
                            {seasonalCalendarData.months.map(month => {
                              const isPlanting = seasonalCalendarData.plantingSeasons[crop].includes(month);
                              const isHarvest = seasonalCalendarData.harvestSeasons[crop].includes(month);
                              
                              return (
                                <td key={month} className="px-2 py-3 text-center">
                                  {isPlanting && isHarvest ? (
                                    <div className="w-6 h-6 rounded-full bg-purple-500 mx-auto\" title="Planting & Harvest Overlap"></div>
                                  ) : isPlanting ? (
                                    <div className="w-6 h-6 rounded-full bg-green-200 border-2 border-green-500 mx-auto" title="Planting Season"></div>
                                  ) : isHarvest ? (
                                    <div className="w-6 h-6 rounded-full bg-amber-200 border-2 border-amber-500 mx-auto" title="Harvest Season"></div>
                                  ) : (
                                    <div className="w-6 h-6 rounded-full bg-gray-100 mx-auto" title="Not Active Season"></div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-green-200 border-2 border-green-500"></div>
                      <span>Planting</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-amber-200 border-2 border-amber-500"></div>
                      <span>Harvest</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                      <span>Planting & Harvest</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-gray-100"></div>
                      <span>Inactive</span>
                    </div>
                  </div>
                </Card>
                
                {/* AI Analysis Request */}
                <Card className="bg-gradient-to-r from-avn-purple-50 to-blue-50 p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="bg-avn-purple-100 p-2 rounded-lg">
                        <Brain className="h-5 w-5 text-avn-purple-600" />
                      </div>
                      <h2 className="text-xl font-bold">AI-Powered Market Analysis</h2>
                    </div>
                    <Badge 
                      variant="purple" 
                      icon={<Zap className="h-3 w-3" />}
                      className="bg-avn-purple-100"
                    >
                      Premium
                    </Badge>
                  </div>
                  
                  <p className="text-gray-700 mb-6">
                    Get personalized insights and forecasts for your specific agricultural interests. 
                    Our AI analyzes global market data, weather patterns, geopolitical events, and more 
                    to deliver actionable intelligence for your business.
                  </p>
                  
                  <button 
                    onClick={() => setShowSignupPrompt(true)}
                    className="bg-avn-purple-600 text-white py-3 px-6 rounded-lg flex items-center gap-2 hover:bg-avn-purple-700 transition-colors"
                  >
                    <Zap className="h-5 w-5" />
                    <span>Request Custom Analysis</span>
                  </button>
                </Card>
              </div>

              {/* Right Column - News and Updates */}
              <div className="space-y-8" id="news">
                {/* Market News */}
                <Card>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <FileText className="h-5 w-5 text-avn-purple-600" />
                      <span>Market News & Updates</span>
                    </h2>
                    <Badge variant="green" icon={<Clock className="h-3 w-3" />}>
                      Live Updates
                    </Badge>
                  </div>
                  
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex-1 min-w-[200px]">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search news..."
                          className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="relative min-w-[150px]">
                      <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <select
                        className="w-full pl-10 pr-4 py-2 border rounded-lg appearance-none bg-white focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                        value={activeCategory}
                        onChange={(e) => setActiveCategory(e.target.value)}
                      >
                        <option value="all">All Categories</option>
                        <option value="market">Market Updates</option>
                        <option value="policy">Policy Changes</option>
                        <option value="technology">Technology</option>
                        <option value="disaster">Disruptions</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                    <AnimatePresence>
                      {filteredNews.length > 0 ? (
                        filteredNews.map((news) => (
                          <motion.div
                            key={news.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.2 }}
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <Badge 
                                variant={
                                  news.impact === 'High' ? 'red' : 
                                  news.impact === 'Medium' ? 'amber' : 'blue'
                                }
                                icon={<AlertTriangle className="h-3 w-3" />}
                              >
                                {news.impact} Impact
                              </Badge>
                              <Badge 
                                variant="gray" 
                                icon={<Clock className="h-3 w-3" />}
                              >
                                {news.date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </Badge>
                              <Badge variant="gray" className="capitalize">
                                {news.category}
                              </Badge>
                            </div>
                            <h3 className="font-bold mb-2">{news.title}</h3>
                            <p className="text-gray-600 text-sm mb-3">{news.content}</p>
                            <div className="flex flex-wrap gap-2">
                              {news.affectedCommodities.map((commodityId) => {
                                const commodity = commoditiesData.find(c => c.id === commodityId);
                                return commodity ? (
                                  <div 
                                    key={commodityId}
                                    className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full"
                                  >
                                    {commodity.icon}
                                    <span>{commodity.name}</span>
                                  </div>
                                ) : null;
                              })}
                            </div>
                          </motion.div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                          <p>No news matching your criteria</p>
                          <button 
                            className="mt-2 text-avn-purple-600 hover:text-avn-purple-700 flex items-center gap-1 mx-auto"
                            onClick={() => {setActiveCategory('all'); setSearchTerm('');}}
                          >
                            <RefreshCw className="h-4 w-4" />
                            <span>Reset filters</span>
                          </button>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                </Card>
                
                {/* Market Alerts */}
                <Card className="bg-gradient-to-br from-amber-50 to-red-50 border border-red-100">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-red-100 p-3 rounded-full">
                      <ShieldAlert className="h-5 w-5 text-red-600" />
                    </div>
                    <h2 className="text-xl font-bold">Price Movement Alerts</h2>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="bg-white border border-red-100 rounded-lg p-3 shadow-sm">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <Coffee className="h-4 w-4 text-amber-700" />
                          <span className="font-medium">Cocoa</span>
                        </div>
                        <Badge variant="red">Critical</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        Price surge of 5.1% today, exceeding forecast by 2.3%. Continued weather concerns in West Africa.
                      </p>
                      <button 
                        onClick={() => {
                          setSelectedCommodity(commoditiesData.find(c => c.id === 'cocoa') || commoditiesData[0]); 
                          setActiveTab('price-trends');
                        }}
                        className="text-xs text-avn-purple-600 hover:text-avn-purple-700 flex items-center gap-1"
                      >
                        <span>View Analysis</span>
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                    
                    <div className="bg-white border border-amber-100 rounded-lg p-3 shadow-sm">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1">
                          <Wheat className="h-4 w-4 text-yellow-700" />
                          <span className="font-medium">Wheat</span>
                        </div>
                        <Badge variant="amber">Moderate</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        Price stabilizing after 3-week decline. US harvest projections revised upward by 5%.
                      </p>
                      <button 
                        onClick={() => {
                          setSelectedCommodity(commoditiesData.find(c => c.id === 'wheat') || commoditiesData[0]); 
                          setActiveTab('price-trends');
                        }}
                        className="text-xs text-avn-purple-600 hover:text-avn-purple-700 flex items-center gap-1"
                      >
                        <span>View Analysis</span>
                        <ArrowRight className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <button 
                      onClick={() => setShowSignupPrompt(true)}
                      className="w-full bg-red-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 hover:bg-red-700 transition-colors"
                    >
                      <AlertTriangle className="h-5 w-5" />
                      <span>Set Up Custom Alerts</span>
                    </button>
                  </div>
                </Card>
                
                {/* Newsletter Signup */}
                <Card className="bg-gradient-to-br from-avn-purple-50 to-blue-50">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-avn-purple-100 p-3 rounded-full">
                      <Globe className="h-5 w-5 text-avn-purple-600" />
                    </div>
                    <h2 className="text-xl font-bold">Market Intelligence Newsletter</h2>
                  </div>
                  
                  <p className="text-gray-700 mb-6">
                    Subscribe to our weekly newsletter featuring expert analysis, market forecasts, and actionable insights for agricultural commodities.
                  </p>
                  
                  {subscribeSuccess ? (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <Check className="h-6 w-6 text-green-500 mx-auto mb-2" />
                      <h4 className="font-medium text-green-800 mb-1">Thank You for Subscribing!</h4>
                      <p className="text-sm text-green-600">
                        You'll receive your first intelligence briefing in the next edition.
                      </p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubscribe} className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-avn-purple-500 focus:border-avn-purple-500"
                          placeholder="your@email.com"
                          value={subscribeEmail}
                          onChange={(e) => setSubscribeEmail(e.target.value)}
                        />
                      </div>
                      
                      {subscribeError && (
                        <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-center gap-2">
                          <X className="h-4 w-4" />
                          <span className="text-sm">{subscribeError}</span>
                        </div>
                      )}
                      
                      <button
                        type="submit"
                        disabled={subscribing}
                        className="w-full bg-avn-purple-600 text-white py-2 px-4 rounded-lg flex items-center justify-center gap-2 hover:bg-avn-purple-700 transition-colors disabled:opacity-70"
                      >
                        {subscribing ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            <span>Subscribing...</span>
                          </>
                        ) : (
                          <>
                            <Globe className="h-4 w-4" />
                            <span>Subscribe</span>
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </Card>
              </div>
            </div>

            {/* Market Intelligence Disclaimer */}
            <div className="mt-12 bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-gray-500 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-medium text-gray-700">Market Intelligence Disclaimer</h3>
                  <p className="text-sm text-gray-500">
                    The information provided on this page is for informational purposes only and does not constitute investment advice.
                    Market data and forecasts are based on our analysis and may not accurately predict future market movements.
                    Commodity prices and market conditions can change rapidly due to various factors including weather, geopolitical events, 
                    and changes in supply and demand. Always conduct your own research before making business decisions.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MarketInsightsPage;